import { useQuery } from '@tanstack/react-query';
import { setCookie } from 'nookies';
import { post } from 'utils/apiWrapper';

import {
  KBOIX_RACES,
  KBOIX_TIPS,
  KBOIX_TIPS_FEEDBACK,
  KBOIX_TIPS_REGISTER,
  KBOIX_USER,
  NNORMAL_WORLD_UNSUBSCRIBE,
  NNORMAL_WORLD_OFFLINE_REGISTER,
  USER_EQUIPMENT,
  USER_EQUIPMENT_AUTHORIZE,
} from 'utils/routes';
import { COOKIES } from 'utils/helpers';
import { logError, type TAppError } from 'utils/error';
import { $fetch, getServerError } from 'utils/request';
import { useLocale } from 'hooks';
import { storage } from 'utils/web-storage';
import { queryKeys } from 'utils/constants/reactQuery';
import { tryCatch } from 'utils/common';

const zappBaseUrl = process.env.NEXT_PUBLIC_ZAPP_API_URL ?? 'https://kboix-api-dev.zapp.dev/api';

export async function registerOfflineOrder(body: FormData) {
  const path = NNORMAL_WORLD_OFFLINE_REGISTER;

  const { data } = await post({
    path,
    data: body,
    headers: {
      'Content-Type': 'multipart/form-data',
    } as TODO,
  });
  if (data.status !== 'OK') {
    throw data;
  }
  return data;
}

type KboixTip = {
  id: string;
  userTipId: string;
  externalId: string;
  title: string;
  link: string;
  text: string;
  like: boolean | null;
  totalLikes: number;
  totalUnlikes: number;
};

export function useTips({ enabled, language }: { enabled: boolean; language: string }) {
  return useQuery({
    queryKey: [queryKeys.nw.tips],
    queryFn: async () => {
      const token = storage.local.get<string>('token', { parse: false });
      if (!token) {
        window.location.reload();
        throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
      }
      const { data, error } = await tryCatch(
        $fetch<KboixTip[]>(
          { base: zappBaseUrl, path: KBOIX_TIPS },
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              Locale: language,
            } as TODO,
          },
        ),
      );
      if (error) {
        if (error.code === 'unauthorized') {
          window.location.reload();
          throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
        }
        throw error;
      }
      return data;
    },
    enabled,
  });
}

export async function sendTipFeedback(id: string, body: Record<string, any>) {
  const token = storage.local.get('token', { parse: false });
  if (!token) {
    throw new Error('No token found');
  }
  const { error } = await tryCatch(
    $fetch<KboixTip>(
      { base: zappBaseUrl, path: KBOIX_TIPS_FEEDBACK(id) },
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      },
    ),
  );
  if (error) {
    if (error.code === 'unauthorized') {
      window.location.reload();
      throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
    }
    throw error;
  }
}

type KboixRace = {
  id: string;
  name: string;
  date: string;
  type: number;
};
export function useRaces(enabled = false) {
  return useQuery({
    queryKey: [queryKeys.nw.races],
    queryFn: async () => {
      const token = storage.local.get<string>('token', { parse: false });
      if (!token) {
        throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
      }
      const { data, error } = await tryCatch(
        $fetch<KboixRace[]>(
          {
            base: zappBaseUrl,
            path: KBOIX_RACES,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            } as TODO,
          },
        ),
      );
      if (error) {
        if (error.code === 'unauthorized') {
          window.location.reload();
          throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
        }
        throw error;
      }
      return data;
    },
    enabled,
  });
}

export async function saveFirstRace(body: Record<string, unknown>) {
  const token = storage.local.get('token');
  if (!token) {
    throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
  }
  const { error } = await tryCatch(
    $fetch(
      {
        base: zappBaseUrl,
        path: KBOIX_TIPS_REGISTER,
      },
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        } as TODO,
        body: JSON.stringify(body),
      },
    ),
  );
  if (error) {
    if (error.code === 'unauthorized') {
      window.location.reload();
      throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
    }
    throw error;
  }
  setCookie(null, COOKIES.KBOIX_TIPS, 'true', {
    sameSite: 'strict',
  });
}

export async function addRace(body: { date: string; name: string; type: number }) {
  const token = storage.local.get('token');
  if (!token) {
    throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
  }
  const { error } = await tryCatch(
    $fetch(
      {
        base: zappBaseUrl,
        path: KBOIX_RACES,
      },
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      },
    ),
  );
  if (error) {
    if (error.code === 'unauthorized') {
      window.location.reload();
      throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
    }
    throw error;
  }
}

export async function deleteRace(id: string) {
  const token = storage.local.get('token');
  if (!token) {
    throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
  }
  const { error } = await tryCatch(
    $fetch(
      {
        base: zappBaseUrl,
        path: `${KBOIX_RACES}/${id}`,
      },
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    ),
  );
  if (error) {
    if (error.code === 'unauthorized') {
      window.location.reload();
      throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
    }
    throw error;
  }
}

type TKboixUser = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  last_period: string | null;
  age: string | null;
  gender: number;
  goals: number;
  tread: number;
  foot_width: number;
  weight_range: number;
  altitude: boolean;
  heat: boolean;
  train_high_carb: boolean;
  train_respiratory: boolean;
  isCompletedForm: boolean;
};

export async function fetchZappUser() {
  const token = storage.local.get<string>('token', { parse: false });
  if (!token) {
    window.location.reload();
    throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
  }
  const { data, error } = await tryCatch(
    $fetch<TKboixUser>(
      { base: zappBaseUrl, path: KBOIX_USER },
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    ),
  );
  if (error) {
    if (error.code === 'unauthorized') {
      window.location.reload();
      throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
    }
    throw error;
  }
  return data;
}
export function useKboixUser(enabled: boolean) {
  return useQuery({
    queryKey: [queryKeys.nw.user],
    queryFn: fetchZappUser,
    enabled,
  });
}

export async function saveKboixUser(body: Record<string, unknown>): Promise<boolean> {
  const token = storage.local.get<string>('token', { parse: false });
  if (!token) {
    window.location.reload();
    throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
  }

  const { error } = await tryCatch(
    $fetch(
      {
        base: zappBaseUrl,
        path: KBOIX_USER,
      },
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      },
    ),
  );

  if (error) {
    if (error.code === 'unauthorized') {
      window.location.reload();
      throw { code: 'unauthorized', message: 'user is not authorized' } satisfies TAppError;
    }
    throw error;
  }

  return true;
}

export async function unsubscribeZappNewsletter(ok: boolean) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No token found');
  }
  const r = await fetch(zappBaseUrl + NNORMAL_WORLD_UNSUBSCRIBE, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ newsletter: ok }),
  });
  if (!r.ok) {
    const e = await r.json();
    logError(e);
    if (r.status === 401) {
      window.location.reload();
    }
    throw e;
  }
}
type TEquipment = {
  id: string;
  product: string;
  name: string;
  shoe_id: number;
  status: 'ACTIVE' | 'PENDING' | 'DELETED';
  strava: boolean;
  parent: string;
  stravaInfo?: {
    name?: string;
    distance_km?: string;
    distance_mi: string;
  };
  suelas?: TEquipment[];
};

type TEquipmentResponse = {
  stravaStatus: string;
  equipment?: TEquipment[];
  clientId: string;
};

export function useEquipment(email: string) {
  const { locale } = useLocale();
  return useQuery({
    queryKey: [queryKeys.nw.equipment],
    queryFn: async () => {
      try {
        const r = await $fetch<TEquipmentResponse>(
          { path: USER_EQUIPMENT },
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            } as TODO,
            body: JSON.stringify({ profile: locale, user: email }),
          },
        );

        return {
          stravaStatus: r?.stravaStatus ?? 'ERROR', // DISCONNECTED, CONNECTED, EXPIRED
          clientId: r?.clientId ?? '',
          equipment:
            r?.equipment?.reduce((acc, eq) => {
              const newEq = {
                ...eq,
                strava: eq.strava ?? false,
                img: `https://nnormal.secure.hoxsolutions.com/is/image/JGVzaG9wMDNtZWRpdW1iZWdnJA==/${eq.product}_L.jpg`,
              };
              if (Array.isArray(eq.suelas) && eq.suelas.length > 0) {
                let km = 0;
                let mi = 0;
                const mappedSuelas = eq.suelas.map((s) => {
                  if (s.stravaInfo?.distance_km) {
                    km += Number(s.stravaInfo.distance_km);
                  }
                  if (s.stravaInfo?.distance_mi) {
                    mi += Number(s.stravaInfo.distance_mi);
                  }
                  if (s.strava === true) {
                    newEq.strava = true;
                  }
                  return {
                    ...s,
                    img: `https://nnormal.secure.hoxsolutions.com/is/image/JGVzaG9wMDNtZWRpdW1iZWdnJA==/${s.product}_L.jpg`,
                  };
                });
                newEq.suelas = mappedSuelas;
                newEq.stravaInfo = {
                  distance_km: String(km),
                  distance_mi: String(mi),
                };
              }
              acc.push(newEq);
              return acc;
            }, [] as TEquipment[]) ?? [],
        };
      } catch (error) {
        return {
          stravaStatus: 'ERROR', // CONNECTED, EXPIRED
          equipment: [],
          clientId: null,
        };
      }
    },
    enabled: !!email,
  });
}

// https://integra.camper.com/eshop-api/api/v1/my-equipment/authorize?authCode=codigo&username=username
type TAuthorizeResponse = {
  status: 'authorize_ok' | 'authorize_ko';
};
export async function authorizeStrava({ code, email, profile }: { code: string; email: string; profile: string }) {
  try {
    const r = await $fetch<TAuthorizeResponse>(
      { path: USER_EQUIPMENT_AUTHORIZE, query: { authCode: code, username: email } },
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        } as TODO,
        body: JSON.stringify({ authCode: code, username: email, profile }),
      },
    );
    return r?.status === 'authorize_ok';
  } catch (error) {
    return false;
  }
}
